import {
  GatewayControleQuinquennal,
  GatewayEntreprise,
} from "@conformite/gateway";
import { usePersonneContext } from "@src/context/PersonneContext";
import { useCQWithAxesQuery } from "@src/data/controle-quinquennal/hook/useCQWithAxesQuery";
import { useEntreprise } from "@src/data/entreprise/hook/useEntreprise";
import { isControleQuinquennalReadOnly } from "@src/helper/ticket.helper";
import {
  axeTypeToQuestionnaireSlug,
  CQParams,
} from "@src/pages/controle-quinquennal/controleQuinquennal.helper";
import { useParams } from "react-router-dom";
import { CQAxeAssujetti } from "./CQAxeAssujetti";
import { CQAxeQuestionnaire } from "./CQAxeQuestionnaire";

type AxeContentProps = {
  idCQ: string;
  idAxe: string;
  liveAxe: GatewayControleQuinquennal.ControleQuinquennalAxeWithNumberOfFiles;
  cq: GatewayControleQuinquennal.ControleQuinquennalAdminResponse;
  entreprise: GatewayEntreprise.EntrepriseAControllerResponseType;
  selectedPersonneId?: string;
  onPersonneSelected: (id: string) => void;
  onQuestionnaireSaved?: () => void;
  onQuestionnaireAnswersChange?: (answers: Record<string, string>) => void;
  onQuestionnaireDirtyChange?: (isDirty: boolean) => void;
};

function AxeContent({
  idCQ,
  idAxe,
  liveAxe,
  cq,
  entreprise,
  selectedPersonneId,
  onPersonneSelected,
  onQuestionnaireSaved,
  onQuestionnaireAnswersChange,
  onQuestionnaireDirtyChange,
}: AxeContentProps) {
  const isReadOnly = isControleQuinquennalReadOnly(cq.pipelineStage);

  if (
    liveAxe.type ===
    GatewayControleQuinquennal.ControleQuinquennalAxeType.ASSUJETTI
  ) {
    return (
      <CQAxeAssujetti
        idCQ={idCQ}
        idAxe={idAxe}
        millesime={cq.millesime}
        siren={entreprise.siren}
        entreprise={entreprise}
        selectedPersonneId={selectedPersonneId}
        onPersonneSelected={onPersonneSelected}
        disabled={isReadOnly}
      />
    );
  }

  const slug = axeTypeToQuestionnaireSlug[liveAxe.type];
  return (
    <CQAxeQuestionnaire
      idCQ={idCQ}
      idAxe={idAxe}
      slug={slug}
      siren={entreprise.siren}
      millesime={cq.millesime}
      disabled={isReadOnly}
      onSaved={onQuestionnaireSaved}
      onAnswersChange={onQuestionnaireAnswersChange}
      onDirtyChange={onQuestionnaireDirtyChange}
    />
  );
}

type AxeBodyProps = {
  onQuestionnaireSaved?: () => void;
  onQuestionnaireAnswersChange?: (answers: Record<string, string>) => void;
  onQuestionnaireDirtyChange?: (isDirty: boolean) => void;
};

export function AxeBody({
  onQuestionnaireSaved,
  onQuestionnaireAnswersChange,
  onQuestionnaireDirtyChange,
}: AxeBodyProps) {
  const { selectedPersonneId, onPersonneSelected } = usePersonneContext();
  const { idEntreprise, idCQ, idAxe } = useParams<CQParams>();

  if (!idCQ || !idAxe || !idEntreprise)
    return <div>Impossible d&apos;afficher la page</div>;

  const { data: cq } = useCQWithAxesQuery(idEntreprise);
  const { data: entreprise } = useEntreprise(cq?.millesime, idEntreprise);

  if (!cq || !entreprise) return null;

  const liveAxe = cq.controleQuinquennalAxes.find((axe) => axe.idAxe === idAxe);
  if (!liveAxe) return <div>Axe non trouvé</div>;

  return (
    <AxeContent
      idCQ={idCQ}
      idAxe={idAxe}
      liveAxe={liveAxe}
      cq={cq}
      entreprise={entreprise}
      selectedPersonneId={selectedPersonneId}
      onPersonneSelected={onPersonneSelected}
      onQuestionnaireSaved={onQuestionnaireSaved}
      onQuestionnaireAnswersChange={onQuestionnaireAnswersChange}
      onQuestionnaireDirtyChange={onQuestionnaireDirtyChange}
    />
  );
}
